import { SwapVert } from "@mui/icons-material";
import { TableSortLabel, Typography } from "@mui/material";
import { flexRender, Header, SortDirection } from "@tanstack/react-table";
import React from "react";
import { FacetMultiSelect } from "./FacetMultiSelect";

export interface DesktopTableHeaderProps<T extends object> {
  header: Header<T, unknown>;
  isFilterableColumn: boolean;
  sortDirection: SortDirection | false;
  filterValue: unknown;
}

function DesktopTableHeaderInner<T extends object>(
  props: DesktopTableHeaderProps<T>,
): React.JSX.Element | null {
  const { header, isFilterableColumn, sortDirection } = props;
  const column = header.column;
  const isPlaceholder = header.isPlaceholder;

  if (isPlaceholder) return null;

  if (isFilterableColumn) {
    return <FacetMultiSelect column={column} filterValue={props.filterValue} />;
  }

  const canSort = column.getCanSort();
  const active = sortDirection !== false;

  const label = flexRender(column.columnDef.header, header.getContext());

  return (
    <TableSortLabel
      IconComponent={active ? undefined : SwapVert}
      active={active}
      direction={sortDirection || undefined}
      disabled={!canSort}
      hideSortIcon={!canSort}
      onClick={canSort ? column.getToggleSortingHandler() : undefined}
      sx={{
        gap: 1,
        color: active ? "text.primary" : "text.secondary",
        "& .MuiTableSortLabel-icon": {
          opacity: active ? 1 : 0.4,
          fontSize: 14,
        },
        cursor: canSort ? "pointer" : "default",
        pointerEvents: canSort ? "auto" : "none",
        "&:hover": {
          color: canSort ? "text.primary" : "text.secondary",
        },
      }}
    >
      <Typography
        component="span"
        sx={{
          fontWeight: 500,
          fontSize: 12,
          color: "inherit",
        }}
      >
        {label}
      </Typography>
    </TableSortLabel>
  );
}

export const DesktopTableHeader = React.memo(
  DesktopTableHeaderInner,
  (prevProps, nextProps) => {
    return (
      prevProps.sortDirection === nextProps.sortDirection &&
      prevProps.filterValue === nextProps.filterValue
    );
  },
) as typeof DesktopTableHeaderInner;

// Standard debugging display name
(DesktopTableHeader as React.NamedExoticComponent<any>).displayName =
  "DesktopTableHeader";
